// src/products/dataroom/components/GroupManagement.js
// File refactored into smaller files
import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit2, Trash2, UserPlus, Eye, Download, Printer, AlertTriangle, Shield, Settings, Search } from 'lucide-react';
import useGroupManagement from '../hooks/useGroupManagement';
import useGroupModals from '../hooks/useGroupModals';
import GroupModals from './GroupManagement/GroupModals';
import PermissionMatrixModal from './Modals/PermissionMatrixModal';
import BiddingGroupAnalysis from './BiddingGroupAnalysis';
import PartiesTeamsDisplay from './PartiesTeamsDisplay';

const GroupManagement = React.memo(({ projectId, user, onMessage, userRole }) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  // Use custom hooks for data management and modal states
  const {
    groups,
    loading,
    fetchGroups,
    createGroup,
    updateGroup,
    deleteGroup,
    addUserToGroup,
    removeUserFromGroup,
    getGroupUsers
  } = useGroupManagement(projectId, userRole, onMessage);

  const {
    showCreateGroup,
    showAddUser,
    showPermissionMatrix,
    confirmDeleteGroup,
    selectedGroup,
    editingGroup,
    newGroupName,
    newGroupDescription,
    newUserEmail,
    openMenuId,
    openCreateGroupModal,
    closeCreateGroupModal,
    openAddUserModal,
    closeAddUserModal,
    openPermissionMatrix,
    closePermissionMatrix,
    startEditGroup,
    cancelEdit,
    openDeleteConfirmation,
    closeDeleteConfirmation,
    toggleMenu,
    updateGroupName,
    updateGroupDescription,
    updateUserEmail,
    getGroupFormData,
    getUserFormData
  } = useGroupModals();


  // FIXED: Updated permission types to match requirements (View, Print, Download)
  const permissionTypes = [
    { 
      id: 'view', 
      name: 'View', 
      icon: Eye, 
      color: 'blue', 
      description: 'Can view file/folder structure only' 
    },
    { 
      id: 'print', 
      name: 'Print', 
      icon: Printer, 
      color: 'purple',
      description: 'View + Print file content'
    },
    { 
      id: 'download', 
      name: 'Download', 
      icon: Download, 
      color: 'green',
      description: 'View + Print + Download file content'
    }
  ];


  useEffect(() => {
    if (projectId) {
      fetchGroups();
    }
  }, [projectId, fetchGroups]);


  // Event handlers that use the hooks
  const handleCreateGroup = async () => {
    const formData = getGroupFormData();
    const success = await createGroup(formData);
    if (success) {
      closeCreateGroupModal();
    }
  };

  const handleUpdateGroup = async () => {
    const formData = getGroupFormData();
    const success = await updateGroup(editingGroup, formData);
    if (success) {
      cancelEdit();
    }
  };

  const handleDeleteGroup = async () => {
    const success = await deleteGroup(confirmDeleteGroup);
    if (success) {
      closeDeleteConfirmation();
    }
  };

  const handleAddUser = async () => {
    const userData = getUserFormData();
    const success = await addUserToGroup(selectedGroup.id, userData.email);
    if (success) {
      closeAddUserModal();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If user is viewer, show access denied (after hooks)
  if (userRole === 'viewer') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-500">You don't have permission to view groups and permissions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header is now part of the global DataroomToolbar */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Groups & Permissions</h1>
          <p className="text-gray-600 mt-1">Manage user groups and their access permissions</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={openPermissionMatrix}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Shield size={16} className="mr-2" />
            Set Permissions
          </button>
          <button
            onClick={openCreateGroupModal}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={16} className="mr-2" />
            Create Group
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search by group name or user..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {/* Future filter options can be added here */}
        </div>
      </div>

      {/* Permission Types Reference */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Permission Types</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {permissionTypes.map((permission) => (
            <div key={permission.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
              <permission.icon className={`h-5 w-5 text-${permission.color}-600 mt-0.5`} />
              <div>
                <h3 className="font-medium text-gray-900">{permission.name}</h3>
                <p className="text-sm text-gray-600">{permission.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Parties & Teams from Setup Wizard */}
      <PartiesTeamsDisplay 
        projectId={projectId}
        userRole={userRole}
        onMessage={onMessage}
      />

      {/* Bidding Group Analysis */}
      <BiddingGroupAnalysis 
        projectId={projectId}
        projectName="Current Project"
        onMessage={onMessage}
      />

      {/* Groups List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Groups ({groups.length})</h2>
        </div>
        
        {(() => {
          const filteredGroups = groups.filter(group => {
            if (!searchTerm) return true;
            const lowerSearchTerm = searchTerm.toLowerCase();
            
            // Check group name
            if (group.name.toLowerCase().includes(lowerSearchTerm)) return true;

            // Check users in group (using dummy data)
            const groupUsers = getGroupUsers(group);
            return groupUsers.some(user => user.name.toLowerCase().includes(lowerSearchTerm) || user.email.toLowerCase().includes(lowerSearchTerm));
          });

          if (filteredGroups.length === 0) {
            return (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No groups yet</h3>
            <p className="text-gray-500 mb-4">Create your first group to organize user permissions</p>
            <button
              onClick={openCreateGroupModal}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Create Group
            </button>
          </div>);
          }
          return (
          <div className="divide-y divide-gray-200">
            {filteredGroups.map((group) => {
              const groupUsers = getGroupUsers(group);
              return (
                <div key={group.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      {editingGroup === group.id ? (
                        <div className="flex items-center space-x-2 flex-1">
                          <input
                            type="text"
                            value={newGroupName}
                            onChange={(e) => updateGroupName(e.target.value)}
                            placeholder="Group name"
                            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                          />
                          <input
                            type="text"
                            value={newGroupDescription}
                            onChange={(e) => updateGroupDescription(e.target.value)}
                            placeholder="Description (optional)"
                            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                          />
                          <button
                            onClick={handleUpdateGroup}
                            className="bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-300 text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Users className="h-5 w-5 text-blue-600" />
                          </div>                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <h3 className="text-lg font-semibold text-gray-900">{group.name}</h3>
                              <button
                                onClick={() => openAddUserModal(group)}
                                className="p-1 text-gray-400 hover:text-green-600 rounded-full hover:bg-green-50 transition-colors"
                                title="Add User to Group"
                              >
                                <UserPlus className="h-5 w-5" />
                              </button>
                              {/* Dataroom tags */}
                              <div className="flex items-center space-x-1">
                                <span 
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
                                  style={{ backgroundColor: '#3B82F620', color: '#3B82F6' }}
                                >
                                  Test project
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-4 mt-1">
                              <span className="text-xs text-gray-500">
                                👥 {group.dataroom_user_groups?.length || 0} members
                              </span>
                              <span className="text-xs text-gray-500">
                                📁 6 folders
                              </span>
                              <span className="text-xs text-gray-500">
                                📄 27 files
                              </span>
                            </div>
                            {group.description && (
                              <p className="text-xs text-gray-600 mt-1 truncate">{group.description}</p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    {editingGroup !== group.id && (
                      <div className="relative ml-4 group-actions-menu">
                        <button
                          onClick={() => toggleMenu(group.id)}
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Manage Group"
                        >
                          <Settings className="h-5 w-5" />
                        </button>
                        {openMenuId === group.id && (
                          <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                            <button
                              onClick={() => { startEditGroup(group); toggleMenu(null); }}
                              className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit Group
                            </button>
                            <button
                              onClick={() => { openDeleteConfirmation(group.id); }}
                              className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Group
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Group Members - Collapsed view */}
                  {groupUsers.length > 0 && (
                    <div className="mt-2 ml-13">
                      <div className="flex flex-wrap gap-1.5">
                        {groupUsers.slice(0, 3).map((groupUser) => (
                          <div
                            key={groupUser.id}
                            className="inline-flex items-center px-2 py-0.5 bg-gray-100 rounded-full text-xs"
                          >
                            <span className="text-gray-700">{groupUser.name}</span>
                            <button
                              onClick={() => removeUserFromGroup(group.id, groupUser.id)}
                              className="ml-1 text-gray-500 hover:text-red-600"
                              title="Remove user"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        {groupUsers.length > 3 && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs text-gray-500">
                            +{groupUsers.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>);
        })()}
      </div>

      {/* Combined Group Modals */}
      <GroupModals
        showCreateGroup={showCreateGroup}
        showAddUser={showAddUser}
        confirmDeleteGroup={confirmDeleteGroup}
        selectedGroup={selectedGroup}
        newGroupName={newGroupName}
        newGroupDescription={newGroupDescription}
        newUserEmail={newUserEmail}
        updateGroupName={updateGroupName}
        updateGroupDescription={updateGroupDescription}
        updateUserEmail={updateUserEmail}
        onCreateGroup={handleCreateGroup}
        onAddUser={handleAddUser}
        onDeleteGroup={handleDeleteGroup}
        closeCreateGroupModal={closeCreateGroupModal}
        closeAddUserModal={closeAddUserModal}
        closeDeleteConfirmation={closeDeleteConfirmation}
      />

      {/* Permission Matrix Modal */}
      {showPermissionMatrix && (
        <PermissionMatrixModal
          isOpen={showPermissionMatrix}
          onClose={closePermissionMatrix}
          projectId={projectId}
          teams={[]} // Will be fetched inside the modal
          onMessage={onMessage}
        />
      )}
    </div>
  );
});

GroupManagement.displayName = 'GroupManagement';

export default GroupManagement;